'use client'

import { toBrandProductListPage } from '@/feature/product-list/services/productList.routing'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { InlineList } from '@/ui/lists/inline-list/InlineList'
import { Title } from '@/ui/text/title/Title'
import { gql } from '@gql-generated/gql'
import { ProductType, type ProductDetailInfosHeader_ProductFragment } from '@gql-generated/graphql'
import { ProductDetailsActionButtons } from './ProductDetailsActionButtons.client'

import { Link } from '@/ui/links/link/Link.client'

gql(/* GraphQL */ `
  fragment ProductDetailInfosHeader_Product on Product {
    brand {
      name
      urlKey
    }
    reference
    name
    supplierReference
    type
  }
`)

type ProductHeaderProps = {
  /** The correlated product */
  product: ProductDetailInfosHeader_ProductFragment
}

export function ProductDetailInfosHeader({ product }: ProductHeaderProps) {
  const { t } = useTranslation(['generic', 'productPage'])

  const brandTitle = product.brand?.name ? (
    <Title size="lg" as="h2" className="font-bold uppercase text-brand-2-900">
      {product.brand.name}
    </Title>
  ) : null

  return (
    <div className="mb-3 flex flex-col gap-1">
      <div className="flex flex-row justify-between">
        {brandTitle &&
          (product.brand?.urlKey ? (
            <Link
              variant="free"
              href={toBrandProductListPage(product.brand.urlKey)}
              onClick={() =>
                product.brand &&
                pushToGtmDataLayer({
                  event: 'gaEvent',
                  eventCategory: 'fiche article / produit',
                  eventAction: 'clic marque',
                  eventLabel: product.brand.name,
                })
              }
            >
              {brandTitle}
            </Link>
          ) : (
            brandTitle
          ))}
        <div className="hidden desktop:block">
          <ProductDetailsActionButtons
            reference={product.reference}
            name={product.name ?? ''}
            type={product.type}
          />
        </div>
      </div>
      <Title as="h1" size="3xl" className="font-bold text-brand-2-900">
        {product.name}
      </Title>
      {(product.reference || product.supplierReference) && (
        <InlineList className="text-sm" alignement="left">
          {product.reference && (
            <InlineList.Item>
              {t(
                `productPage:header.${product.type === ProductType.Article ? 'refTextWithValue' : 'pCode'}`,
                { ref: product.reference },
              )}
            </InlineList.Item>
          )}
          {product.supplierReference && (
            <InlineList.Item>
              {t('productPage:header.refSupplier', {
                ref: product.supplierReference,
                interpolation: { escapeValue: false },
              })}
            </InlineList.Item>
          )}
        </InlineList>
      )}
    </div>
  )
}
